import { AfterViewInit, Directive, ElementRef, Input, OnDestroy } from '@angular/core';
import { fromEvent, of, Subject } from 'rxjs';
import { filter, takeUntil, switchMap } from 'rxjs/operators';

import { InterstitialModalComponent } from '@finder/shared/components/interstitial-modal/interstitial-modal.component';
import { INTERSTITIAL_CLASSES, INTERSTITIAL_LABELS } from './interstitial-trigger.defaults';
import { ModalService } from '@finder/shared/components/modal/modal.service';
import { ModalComponent } from '@finder/shared/components/modal/modal.component';

@Directive({
    selector: '[finderInterstitialTrigger]',
    standalone: true,
})
export class InterstitialTriggerDirective implements AfterViewInit, OnDestroy {

    // set default value in case the bundle service fails
    @Input() interstitialLabels: any = INTERSTITIAL_LABELS;

    interstitialModal: ModalComponent;
    private destroy$ = new Subject<void>();

    constructor(
        private elementRef: ElementRef,
        private modalService: ModalService,
    ) { }

    /* istanbul ignore next */
    getElementRef(): Element {
        return this.elementRef.nativeElement;
    }

    ngAfterViewInit() {
        fromEvent(this.getElementRef(), 'click')
            .pipe(
                filter((event: any) => {
                    const element: HTMLElement = this.findAnchor(event.target);

                    // take only anchors with interstitial classes applied
                    return element instanceof HTMLAnchorElement &&
                        INTERSTITIAL_CLASSES.some(cl => element.classList.contains(cl));
                }),
                switchMap((event: any) => {
                    // avoid the event bubble to the syndicated header
                    // that way we make sure only our modal is triggered
                    event.stopPropagation();
                    // intercept redirection
                    event.preventDefault();

                    // Get the href data from the target or it's closest anchor
                    const element = this.findAnchor(event.target) as HTMLAnchorElement;

                    // if the modal is already created, we just update the data
                    if (this.interstitialModal) {
                        this.interstitialModal.config.data = {
                            ...this.interstitialModal.config.data,
                            href: element.href
                        };

                        return of(true);
                    }

                    this.interstitialModal = this.modalService.create(
                        InterstitialModalComponent,
                        {
                            title: this.interstitialLabels.title,
                            data: {
                                labels: this.interstitialLabels,
                                href: element.href
                            }
                        }
                    );

                    // wait until the modal is created and ready to launch
                    return this.interstitialModal.ready();
                }),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.interstitialModal.open());
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    findAnchor(element: HTMLElement): HTMLElement | HTMLAnchorElement {
        // The item clicked might not be an anchor. If not move up the structure to find one
        if (!(element instanceof HTMLAnchorElement)) {
            return element.closest('a') || element;
        }

        return element;
    }
}
